// kanjo/Mailbox.jsx — admin-only support@/hello@ inbox.
//
// Two-pane on desktop / stacked on mobile:
//   - left: list of threads (open or replied), newest first
//   - right: selected thread (messages stacked oldest first), with a
//            reply composer at the bottom (Subject pre-filled with
//            "Re: ", body empty, "Draft with Claude" button)
//
// All routes are admins-only — the API enforces it and the UI gates
// before any fetch.

async function mboxFetch(path, init) {
  const cfg = window.kanjo.getConfig();
  const at = await window.kanjo.auth.getAccessToken();
  const res = await fetch(`${cfg.apiBaseUrl}${path}`, {
    ...(init || {}),
    headers: {
      ...(init?.headers || {}),
      authorization: `Bearer ${at}`,
      ...(init?.body ? { 'content-type': 'application/json' } : {}),
    },
  });
  if (res.status === 403) { const e = new Error('admin only'); e.code = 403; throw e; }
  if (!res.ok) { const e = new Error(`${res.status} ${await res.text().catch(() => '')}`); e.code = res.status; throw e; }
  return res.json();
}

function fmtWhen(iso, locale) {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString(locale || 'en-US', { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString(locale || 'en-US', { month: 'short', day: 'numeric' });
}

function ThreadListItem({ thread, selected, onSelect, onDelete, locale }) {
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 4, marginBottom: 6 }}>
      <button onClick={() => onSelect(thread.threadId)}
        style={{
          flex: 1, minWidth: 0, display: 'block', textAlign: 'left',
          padding: '12px 14px', borderRadius: 10, border: 'none',
          background: selected ? 'var(--accent-soft,#e7edfe)' : 'transparent',
          cursor: 'pointer',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--on)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {thread.from}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--on-dim)', flexShrink: 0 }}>
            {fmtWhen(thread.lastReceivedAt, locale)}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--on-soft)', marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {thread.subject || '(no subject)'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--on-dim)', marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {thread.preview || ''}
        </div>
      </button>
      <button onClick={() => onDelete(thread.threadId)} title="Delete" aria-label="Delete thread"
        style={{
          flexShrink: 0, width: 40, border: '1px solid var(--rule,#e4e6ea)', borderRadius: 10,
          background: 'transparent', color: 'var(--on-dim)', cursor: 'pointer', fontSize: 15,
        }}>🗑</button>
    </div>
  );
}

function MessageBubble({ msg, locale }) {
  const isOut = msg.direction === 'out';
  return (
    <div style={{
      background: isOut ? 'var(--accent-soft,#e7edfe)' : 'var(--surface,#fff)',
      border: '1px solid var(--rule,#e4e6ea)', borderRadius: 12,
      padding: '12px 14px', marginBottom: 10,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, color: 'var(--ink-3)' }}>
        <span>{isOut ? `Sent · ${msg.from}` : `From: ${msg.from}`}</span>
        <span>{fmtWhen(msg.receivedAt, locale)}</span>
      </div>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--ink)', marginTop: 4 }}>
        {msg.subject || '(no subject)'}
      </div>
      <pre style={{
        marginTop: 8, marginBottom: 0,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        fontFamily: 'inherit', fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink-2)',
      }}>{msg.bodyText || ''}</pre>
    </div>
  );
}

function MailboxScreen({ dark = false, persona = 'owner' }) {
  useT();
  const [authz, setAuthz] = React.useState('checking');
  const [status, setStatus] = React.useState('open');  // 'open' | 'replied'
  const [threads, setThreads] = React.useState(null);
  const [activeId, setActiveId] = React.useState(null);
  const [thread, setThread] = React.useState(null);
  const [composer, setComposer] = React.useState({ subject: '', body: '' });
  const [drafting, setDrafting] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [info, setInfo] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const locale = 'en-US';

  React.useEffect(() => {
    (async () => {
      try {
        const c = await window.kanjo?.auth?.currentClaims?.();
        let g = c && c['cognito:groups'];
        if (typeof g === 'string') g = g.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
        setAuthz(Array.isArray(g) && g.includes('admins') ? 'ok' : 'denied');
      } catch { setAuthz('denied'); }
    })();
  }, []);

  const loadThreads = React.useCallback(async () => {
    setErr(null);
    try {
      const r = await mboxFetch(`/admin/mailbox?status=${status}`);
      setThreads(r.threads || []);
    } catch (e) { setErr(String(e.message || e)); }
  }, [status]);

  React.useEffect(() => { if (authz === 'ok') loadThreads(); }, [authz, loadThreads]);

  const openThread = async (id) => {
    setActiveId(id);
    setThread(null); setComposer({ subject: '', body: '' }); setInfo(null);
    try {
      const r = await mboxFetch(`/admin/mailbox/${encodeURIComponent(id)}`);
      setThread(r);
      const last = (r.messages || []).slice().reverse().find((m) => m.direction === 'in') || r.messages[r.messages.length - 1];
      const subj = last?.subject || '';
      setComposer({ subject: subj.startsWith('Re:') || subj.startsWith('RE:') ? subj : `Re: ${subj}`, body: '' });
    } catch (e) { setErr(String(e.message || e)); }
  };

  const onDraft = async () => {
    if (!activeId) return;
    setDrafting(true); setInfo(null);
    try {
      const r = await mboxFetch(`/admin/mailbox/${encodeURIComponent(activeId)}/draft`, { method: 'POST' });
      if (r.needsHuman) {
        setInfo({ kind: 'declined', msg: r.reason || 'Claude declined to draft.' });
      } else if (r.draft) {
        setComposer((c) => ({ ...c, body: r.draft }));
        setInfo({ kind: 'ok', msg: 'Draft inserted. Review and edit before sending.' });
      }
    } catch (e) { setErr(String(e.message || e)); }
    finally { setDrafting(false); }
  };

  const onDelete = async (id) => {
    if (!id) return;
    if (!window.confirm('Delete this thread from the inbox? The original email stays archived in S3, but it will be removed from this list.')) return;
    setErr(null);
    try {
      await mboxFetch(`/admin/mailbox/${encodeURIComponent(id)}`, { method: 'DELETE' });
      setThreads((ts) => (ts || []).filter((t) => t.threadId !== id));
      if (activeId === id) { setActiveId(null); setThread(null); setComposer({ subject: '', body: '' }); }
      setInfo(null);
    } catch (e) { setErr(String(e.message || e)); }
  };

  const onSend = async () => {
    if (!activeId || !composer.subject.trim() || !composer.body.trim()) return;
    setSending(true); setInfo(null);
    try {
      await mboxFetch(`/admin/mailbox/${encodeURIComponent(activeId)}/reply`, {
        method: 'POST',
        body: JSON.stringify({ subject: composer.subject.trim(), bodyText: composer.body.trim() }),
      });
      setInfo({ kind: 'ok', msg: '✓ Reply sent.' });
      await openThread(activeId);
      await loadThreads();
    } catch (e) { setErr(String(e.message || e)); }
    finally { setSending(false); }
  };

  if (authz === 'checking') {
    return <KShell tab="reports" persona={persona}><KHeader back title="Inbox"/><div style={{ padding: 40, textAlign: 'center', color: 'var(--on-dim)' }}>Checking access…</div></KShell>;
  }
  if (authz === 'denied') {
    return <KShell tab="reports" persona={persona}><KHeader back title="Inbox"/><Empty title="Admins only" body="This inbox is restricted to system administrators."/></KShell>;
  }

  return (
    <KShell dark={dark} tab="reports" persona={persona}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <KHeader back title="Inbox" subtitle="support@ + hello@getkanjo.com"/>

        {err && <ErrorBanner style={{ margin: '0 16px 8px' }}>{err}</ErrorBanner>}

        {/* Status switcher */}
        <div style={{ display: 'flex', gap: 6, padding: '0 16px 12px' }}>
          {[['open', 'Open'], ['replied', 'Replied']].map(([id, label]) => {
            const on = status === id;
            return (
              <button key={id} onClick={() => { setStatus(id); setActiveId(null); setThread(null); }}
                style={{
                  padding: '6px 12px', borderRadius: 999, border: '1px solid var(--rule)',
                  background: on ? 'var(--ink)' : 'transparent',
                  color: on ? 'var(--surface,#fff)' : 'var(--on-soft)',
                  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
                }}>{label}</button>
            );
          })}
          <button onClick={loadThreads}
            style={{
              padding: '6px 12px', borderRadius: 999, border: '1px solid var(--rule)',
              background: 'transparent', color: 'var(--on-soft)',
              fontSize: 12.5, fontWeight: 500, cursor: 'pointer', marginLeft: 'auto',
            }}>Refresh</button>
        </div>

        <div style={{ flex: 1, minHeight: 0, padding: '0 16px 16px', overflowY: 'auto' }}>

          {/* Thread list — hidden when a thread is open (back button restores it) */}
          {!activeId && (
            <div>
              {threads === null && <div style={{ color: 'var(--on-dim)', fontSize: 13, padding: 10 }}>Loading…</div>}
              {threads && threads.length === 0 && <div style={{ color: 'var(--on-dim)', fontSize: 13, padding: 10 }}>No {status} threads.</div>}
              {threads && threads.map((th) => (
                <ThreadListItem key={th.threadId} thread={th} selected={false}
                  onSelect={openThread} onDelete={onDelete} locale={locale}/>
              ))}
            </div>
          )}

          {/* Selected thread — messages stacked with reply composer below */}
          {activeId && (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={() => { setActiveId(null); setThread(null); setComposer({ subject: '', body: '' }); setInfo(null); }}
                  style={{ background: 'transparent', border: 'none',
                    color: 'var(--accent,#5b8def)', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                    padding: '6px 4px 12px' }}>← Back to inbox</button>
                <button onClick={() => onDelete(activeId)} title="Delete thread"
                  style={{ background: 'transparent', border: '1px solid var(--rule,#e4e6ea)', borderRadius: 8,
                    color: 'var(--danger,#c0392b)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
                    padding: '6px 12px' }}>🗑 Delete</button>
              </div>
              {!thread && <div style={{ color: 'var(--on-dim)', fontSize: 13, padding: 12 }}>Loading…</div>}
              {thread && (
              <>
                {(thread.messages || []).map((m) => (
                  <MessageBubble key={m.messageId} msg={m} locale={locale}/>
                ))}
                <div style={{ borderTop: '1px solid var(--rule)', paddingTop: 12, marginTop: 6 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                    <button onClick={onDraft} disabled={drafting}
                      style={{
                        padding: '7px 14px', borderRadius: 8, border: '1px solid var(--accent,#5b8def)',
                        background: 'transparent', color: 'var(--accent,#5b8def)',
                        fontSize: 13, fontWeight: 600, cursor: drafting ? 'default' : 'pointer',
                        opacity: drafting ? 0.6 : 1,
                      }}>{drafting ? 'Drafting…' : 'Draft with Claude'}</button>
                    {info && (
                      <span style={{ fontSize: 12,
                        color: info.kind === 'declined' ? 'var(--warning,#9a6700)'
                             : info.kind === 'ok' ? 'var(--positive,#2e9e5b)' : 'var(--on-soft)' }}>
                        {info.kind === 'declined' ? `Claude declined: ${info.msg}` : info.msg}
                      </span>
                    )}
                  </div>
                  <input value={composer.subject}
                    onChange={(e) => setComposer((c) => ({ ...c, subject: e.target.value }))}
                    placeholder="Subject"
                    style={{
                      width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--rule)',
                      background: 'var(--surface)', color: 'var(--ink)', fontSize: 14, marginBottom: 8, boxSizing: 'border-box',
                    }}/>
                  <textarea value={composer.body}
                    onChange={(e) => setComposer((c) => ({ ...c, body: e.target.value }))}
                    placeholder="Your reply…"
                    style={{
                      width: '100%', minHeight: 180, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--rule)',
                      background: 'var(--surface)', color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit',
                      resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.55,
                    }}/>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    <button onClick={onSend} disabled={sending || !composer.subject.trim() || !composer.body.trim()}
                      className="btn primary"
                      style={{ opacity: sending ? 0.6 : 1, cursor: sending ? 'default' : 'pointer' }}>
                      {sending ? 'Sending…' : 'Send reply'}
                    </button>
                  </div>
                </div>
              </>
              )}
            </div>
          )}

        </div>
      </div>
    </KShell>
  );
}

Object.assign(window, { MailboxScreen });
